import type { NextRequest } from 'next/server';

/**
 * Debug logging utility for authentication and API endpoints
 */
export class DebugLogger {
  private static isDebugEnabled(): boolean {
    return process.env.NODE_ENV === 'development' && process.env.DEBUG_LOGGING === 'true';
  }

  private static isAuthDebugEnabled(): boolean {
    return process.env.NODE_ENV === 'development' && process.env.DEBUG_AUTH === 'true';
  }

  private static isErrorDebugEnabled(): boolean {
    return process.env.NODE_ENV === 'development' && process.env.DEBUG_ERROR === 'true';
  }

  private static isGoogleDebugEnabled(): boolean {
    return this.isDebugEnabled() && process.env.DEBUG_GOOGLE === 'true';
  }

  private static isMicrosoftDebugEnabled(): boolean {
    return this.isDebugEnabled() && process.env.DEBUG_MICROSOFT === 'true';
  }

  private static formatTimestamp(): string {
    return new Date().toISOString();
  }

  private static formatRequestInfo(request: NextRequest): object {
    return {
      method: request.method,
      url: request.url,
      headers: {
        'user-agent': request.headers.get('user-agent'),
        'origin': request.headers.get('origin'),
        'referer': request.headers.get('referer'),
        'authorization': request.headers.get('authorization') ? '[PRESENT]' : '[MISSING]',
        'content-type': request.headers.get('content-type'),
      },
      ip: this.getClientIP(request),
    };
  }

  private static getClientIP(request: NextRequest): string | null {
    const forwarded = request.headers.get('x-forwarded-for');
    const realIP = request.headers.get('x-real-ip');
    const cfConnectingIP = request.headers.get('cf-connecting-ip');
    
    if (forwarded) {
      return forwarded.split(',')[0].trim();
    }
    if (realIP) {
      return realIP;
    }
    if (cfConnectingIP) {
      return cfConnectingIP;
    }
    
return null;
  }

  /**
   * Log authentication-related debug information
   */
  static logAuth(operation: string, details: any, request?: NextRequest): void {
    if (!this.isAuthDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      operation: `[AUTH] ${operation}`,
      details,
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.log('🔐 AUTH DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log API endpoint debug information
   */
  static logAPI(endpoint: string, operation: string, details: any, request?: NextRequest): void {
    if (!this.isDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      endpoint: `[API] ${endpoint}`,
      operation,
      details,
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.log('🌐 API DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log OAuth-related debug information
   */
  static logOAuth(provider: 'google' | 'microsoft', operation: string, details: any, request?: NextRequest): void {
    if (provider === 'google' && !this.isGoogleDebugEnabled()) return;
    if (provider === 'microsoft' && !this.isMicrosoftDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      provider: `[OAUTH-${provider.toUpperCase()}]`,
      operation,
      details,
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.log('🔑 OAUTH DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log calendar-related debug information
   */
  static logCalendar(provider: 'google' | 'microsoft', operation: string, details: any, request?: NextRequest): void {
    if (provider === 'google' && !this.isGoogleDebugEnabled()) return;
    if (provider === 'microsoft' && !this.isMicrosoftDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      provider: `[CALENDAR-${provider.toUpperCase()}]`,
      operation,
      details,
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.log('📅 CALENDAR DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log error information with context
   */
  static logError(context: string, error: any, request?: NextRequest): void {
    if (!this.isErrorDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      context: `[ERROR] ${context}`,
      error: {
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        ...(error?.response && {
          response: {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          }
        })
      },
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.error('❌ ERROR DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log performance metrics
   */
  static logPerformance(operation: string, duration: number, details?: any): void {
    if (!this.isDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      operation: `[PERF] ${operation}`,
      duration: `${duration}ms`,
      details
    };

    console.log('⚡ PERF DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log token-related operations
   */
  static logToken(operation: string, details: any, request?: NextRequest): void {
    if (!this.isDebugEnabled()) return;

    // Sanitize sensitive token data
    const sanitizedDetails = {
      ...details,
      token: details.token ? '[TOKEN_PRESENT]' : '[TOKEN_MISSING]',
      accessToken: details.accessToken ? '[ACCESS_TOKEN_PRESENT]' : '[ACCESS_TOKEN_MISSING]',
      refreshToken: details.refreshToken ? '[REFRESH_TOKEN_PRESENT]' : '[REFRESH_TOKEN_MISSING]',
      clientId: details.clientId ? '[CLIENT_ID_PRESENT]' : '[CLIENT_ID_MISSING]',
      clientSecret: details.clientSecret ? '[CLIENT_SECRET_PRESENT]' : '[CLIENT_SECRET_MISSING]',
    };

    const logData = {
      timestamp: this.formatTimestamp(),
      operation: `[TOKEN] ${operation}`,
      details: sanitizedDetails,
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.log('🎫 TOKEN DEBUG:', JSON.stringify(logData, null, 2));
  }

  /**
   * Log database operations
   */
  static logDatabase(operation: string, details: any, request?: NextRequest): void {
    // Check if this is a Google or Microsoft specific operation
    const isGoogleOp = operation.toLowerCase().includes('google');
    const isMicrosoftOp = operation.toLowerCase().includes('msft') || operation.toLowerCase().includes('microsoft');
    
    if (isGoogleOp && !this.isGoogleDebugEnabled()) return;
    if (isMicrosoftOp && !this.isMicrosoftDebugEnabled()) return;
    if (!isGoogleOp && !isMicrosoftOp && !this.isDebugEnabled()) return;

    const logData = {
      timestamp: this.formatTimestamp(),
      operation: `[DB] ${operation}`,
      details,
      ...(request && { request: this.formatRequestInfo(request) })
    };

    console.log('🗄️ DB DEBUG:', JSON.stringify(logData, null, 2));
  }
}

/**
 * Performance measurement utility
 */
export class PerformanceTimer {
  private startTime: number;
  private operation: string;

  constructor(operation: string) {
    this.operation = operation;
    this.startTime = Date.now();
  }

  end(details?: any): number {
    const duration = Date.now() - this.startTime;
    DebugLogger.logPerformance(this.operation, duration, details);
    
return duration;
  }
}

/**
 * Helper function to create a performance timer
 */
export function startTimer(operation: string): PerformanceTimer {
  return new PerformanceTimer(operation);
}
